/**
 * Prompt @-mention catalog and plain-text <-> TipTap document serialization.
 *
 * Storage format is plain text containing canonical tokens:
 *   @[field:team_size]  @[skill:scheduling]  @[tool:show_booking_calendar]  @[context:company.industry]
 *
 * The backend resolves these at prompt-build time (prompt_reference_service.py
 * holds the mirror of MENTION_REGEX). The editor renders them as pills; these
 * helpers are pure functions so the round-trip is unit-testable without an
 * editor instance.
 */

import { AgentSkill } from '../tabs/types';
import {
  WorkflowConditionField,
  buildWorkflowConditionFields,
} from './workflowConditionFields';

// Mirror of backend MENTION_PATTERN in prompt_reference_service.py.
export const MENTION_REGEX = /@\[(\w+):([A-Za-z0-9_.\-]+)\]/g;

export type MentionableGroup =
  | 'Fields'
  | 'Questions'
  | 'AI Labels'
  | 'Visitor Data'
  | 'Account'
  | 'Skills'
  | 'Objectives'
  | 'Tools'
  | 'Team';

/** Org user shape as returned by GET /users/ (subset we need). */
export interface MentionableUser {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

/** Objective definition shape (subset we need for the mention catalog). */
export interface MentionableObjective {
  key: string;
  name?: string;
}

export interface Mentionable {
  /** Canonical token body, e.g. "field:team_size" or "context:company.industry". */
  token: string;
  label: string;
  group: MentionableGroup;
}

const REFERENCEABLE_TOOLS: Array<{ name: string; label: string }> = [
  { name: 'update_form', label: 'Update form' },
  { name: 'ask_question', label: 'Ask a question' },
  { name: 'search_knowledge', label: 'Search knowledge' },
  { name: 'web_search', label: 'Web search' },
  { name: 'web_fetch', label: 'Web fetch' },
  { name: 'show_booking_calendar', label: 'Show booking calendar' },
  { name: 'require_contact_info', label: 'Require contact info' },
  { name: 'escalate_to_human', label: 'Escalate to human' },
  { name: 'navigate', label: 'Navigate' },
  { name: 'get_crm_contact', label: 'CRM contact record' },
  { name: 'get_crm_account', label: 'CRM account' },
  { name: 'get_crm_open_deals', label: 'CRM open deals' },
  { name: 'get_crm_recent_activity', label: 'CRM recent activity' },
];

function groupForConditionField(field: WorkflowConditionField): MentionableGroup {
  if (field.badgeType === 'question') return 'Questions';
  if (field.badgeType === 'ai_label') return 'AI Labels';
  if (field.badgeType === 'field') return 'Fields';
  if (field.badgeType === 'account') return 'Account';
  return 'Visitor Data';
}

/**
 * Everything the @-menu can insert. Form-derived entries become field tokens;
 * the rest of the condition catalog (system/person/company data) becomes
 * context tokens, so the @-menu and the condition builder stay consistent.
 */
export function buildMentionables(
  form: any[],
  skills: AgentSkill[],
  options?: {
    excludeSkillId?: string;
    users?: MentionableUser[];
    objectives?: MentionableObjective[];
  }
): Mentionable[] {
  const mentionables: Mentionable[] = [];

  for (const field of buildWorkflowConditionFields(form || [])) {
    const group = groupForConditionField(field);
    if (group === 'Visitor Data' || group === 'Account') {
      mentionables.push({ token: `context:${field.key}`, label: field.label, group });
    } else {
      const name = field.legacyKey || field.key.replace(/^form\./, '');
      mentionables.push({ token: `field:${name}`, label: field.label, group });
    }
  }

  for (const skill of skills || []) {
    if (!skill?.id || skill.id === options?.excludeSkillId) continue;
    mentionables.push({
      token: `skill:${skill.id}`,
      label: skill.name || skill.id,
      group: 'Skills',
    });
  }

  // Objectives resolve by key (the stable slug), mirroring the backend's
  // objectives kwarg in prompt_reference_service.resolve.
  for (const objective of options?.objectives || []) {
    if (!objective?.key) continue;
    mentionables.push({
      token: `objective:${objective.key}`,
      label: objective.name || objective.key,
      group: 'Objectives',
    });
  }

  for (const tool of REFERENCEABLE_TOOLS) {
    mentionables.push({ token: `tool:${tool.name}`, label: tool.label, group: 'Tools' });
  }

  // Team members: the backend inlines name + email at prompt-build time, so
  // instructions can route by person ("send enterprise deals to @Grace")
  // without hand-typed emails that go stale.
  for (const user of options?.users || []) {
    if (!user?.id) continue;
    const name =
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
    if (!name) continue;
    mentionables.push({ token: `user:${user.id}`, label: name, group: 'Team' });
  }

  return mentionables;
}

export function findMentionable(
  mentionables: Mentionable[],
  token: string
): Mentionable | undefined {
  return mentionables.find((m) => m.token === token);
}

// ---------------------------------------------------------------------------
// Plain text <-> TipTap JSON document
//
// Conventions: one paragraph per line, paragraphs join with "\n", hard breaks
// map to "\n". This keeps serialize(deserialize(text)) === text for any plain
// text, including blank lines.
// ---------------------------------------------------------------------------

type DocNode = Record<string, any>;

export function textToDoc(text: string, mentionables: Mentionable[]): DocNode {
  const lines = (text || '').split('\n');
  const paragraphs = lines.map((line) => {
    const content: DocNode[] = [];
    let lastIndex = 0;
    const regex = new RegExp(MENTION_REGEX.source, 'g');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        content.push({ type: 'text', text: line.slice(lastIndex, match.index) });
      }
      const token = `${match[1]}:${match[2]}`;
      const known = findMentionable(mentionables, token);
      // User tokens carry a uuid — meaningless as a label. Fall back to a
      // neutral name until the async users fetch lands (MentionEditor
      // relabels pills in place once it does) or forever for removed users,
      // mirroring the backend's "a team member" rendering.
      const fallbackLabel = match[1] === 'user' ? 'Team member' : match[2];
      content.push({
        type: 'promptMention',
        attrs: { id: token, label: known?.label || fallbackLabel, group: known?.group },
      });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      content.push({ type: 'text', text: line.slice(lastIndex) });
    }
    return content.length > 0
      ? { type: 'paragraph', content }
      : { type: 'paragraph' };
  });
  return { type: 'doc', content: paragraphs };
}

export function docToText(doc: DocNode): string {
  const lines: string[] = [];
  for (const paragraph of doc?.content || []) {
    const parts: string[] = [];
    walkInline(paragraph.content || [], parts, lines);
    lines.push(parts.join(''));
  }
  return lines.join('\n');
}

function walkInline(nodes: DocNode[], parts: string[], lines: string[]): void {
  for (const node of nodes) {
    if (node.type === 'text') {
      parts.push(node.text || '');
    } else if (node.type === 'promptMention') {
      parts.push(`@[${node.attrs?.id || ''}]`);
    } else if (node.type === 'hardBreak') {
      // Flush the current line; a hard break behaves like a newline.
      lines.push(parts.join(''));
      parts.length = 0;
    } else if (node.content) {
      walkInline(node.content, parts, lines);
    }
  }
}
